<script setup lang="ts">
import { isAppUsing } from '@@/bits/flip'
import { NON_STREAM_STANDARD_MAX_POST_SIZE, NON_STREAM_WIDE_MAX_POST_SIZE } from '@@/bits/post_size'
import type { Group } from '@@/bits/surface_posts_grouping_helper'
import { WallWishGroupByTypes } from '@@/enums'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfaceContainerSizeStore } from '@@/pinia/surface_container_size'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import { useSurfacePostsDragAndDropStore } from '@@/pinia/surface_posts_drag_and_drop'
import { useSurfaceWishArrangementStore } from '@@/pinia/surface_wish_arrangement'
import { useWindowSizeStore } from '@@/pinia/window_size'
import { computePostIndexFromPost } from '@@/surface/sorter'
import type { Id } from '@@/types'
import SurfaceLazy from '@@/vuecomponents/SurfaceLazy.vue'
import SurfacePostGroupHeaderSection from '@@/vuecomponents/SurfacePostGroupHeaderSection.vue'
import SurfacePostGroupHeaderSectionOld from '@@/vuecomponents/SurfacePostGroupHeaderSectionOld.vue'
import SurfacePost from '@@/vuecomponents/surface_post.vue'
import { useDragSortableInGroups } from '@@/vuecomposables/surface_posts_drag_sortable_in_groups'
import { storeToRefs } from 'pinia'
import { computed } from 'vue'

withDefaults(
  defineProps<{
    group: Group
    reactionIdsByPostId: Record<Id, Id[]>
    commentIdsByPostId: Record<Id, Id[]>
    xSectionHeader?: boolean
  }>(),
  {
    reactionIdsByPostId: () => ({}),
    commentIdsByPostId: () => ({}),
    xSectionHeader: true,
  },
)

const surfacePostsStore = useSurfacePostsStore()
const surfacePostsDragAndDropStore = useSurfacePostsDragAndDropStore()

const { backgroundLuminance, isPostSizeStandard, isPostSizeWide, isSectioned } = storeToRefs(useSurfaceStore())
const { isAPostDragging, groupIdUnderPostBeingDragged } = storeToRefs(surfacePostsDragAndDropStore)
const { allSortedPostsByGroup } = storeToRefs(surfacePostsStore)
const { wishGroupBy, isGroupByNone } = storeToRefs(useSurfaceWishArrangementStore())
const {
  isContainerSmallerThanTabletPortrait,
  surfaceContainerWidth,
  isContainerSmallerThanTabletLandscape,
  isContainerSmallerThanDesktop,
} = storeToRefs(useSurfaceContainerSizeStore())

const { shouldDisplayPost } = surfacePostsStore
const { canShowDraggingOverlayOnGroup } = surfacePostsDragAndDropStore
const { windowHeight } = storeToRefs(useWindowSizeStore())

const overlayHeight = computed(() => {
  const hoveredSectionQuery = `#section-${groupIdUnderPostBeingDragged.value}`
  const hoveredSectionElementHeight = document.querySelector(hoveredSectionQuery)?.getBoundingClientRect().height
  // add extra height to the overlay so it extends beyond posts and section header
  return hoveredSectionElementHeight ? hoveredSectionElementHeight + 32 : 0
})

const overlayWidth = computed(() => {
  const hoveredSectionQuery = `#section-${groupIdUnderPostBeingDragged.value}`
  const sectionWidth = document.querySelector(hoveredSectionQuery)?.getBoundingClientRect().width
  // add extra width to the overlay so it extends beyond posts
  return sectionWidth ? sectionWidth + 32 : 0
})

useDragSortableInGroups({
  onDragStart: ({ groupId }) => {
    isAPostDragging.value = true
    groupIdUnderPostBeingDragged.value = groupId
  },
  onDragEnd: ({ postCid, postId, groupId }) => {
    isAPostDragging.value = false
    if (postCid && postId) {
      const sectionId =
        typeof groupId === 'number' && wishGroupBy.value === WallWishGroupByTypes.Section ? groupId : undefined
      const prevWish = document
        .querySelector(`#wish-${postId}`)
        ?.closest('.wish-wrapper')
        ?.previousElementSibling?.querySelector('.wish') as HTMLElement | undefined
      const nextWish = document
        .querySelector(`#wish-${postId}`)
        ?.closest('.wish-wrapper')
        ?.nextElementSibling?.querySelector('.wish') as HTMLElement | undefined
      const { sortIndex, gapSize } = computePostIndexFromPost(`#wish-${postId}`, {
        sectionId,
        prevId: prevWish?.id,
        nextId: nextWish?.id,
      })
      surfacePostsStore.sortPost({
        postCid,
        sortIndex: sortIndex ?? undefined,
        sectionId,
        gapSize,
      })
    }
    groupIdUnderPostBeingDragged.value = undefined
  },
  format: 'matrix',
})

const minHeight = computed(() => {
  // Set minHeight as 1/4 of windowHeight since we assume that each post fills 1/4 of the screen's height
  return windowHeight.value / 4
})
const minWidth = computed(() => {
  const sidePanelWidth = isContainerSmallerThanTabletLandscape.value ? 0 : 72 // 72 is the size of the settings side panel
  const availableWidth = surfaceContainerWidth.value - sidePanelWidth - 14 // 14 is the scrollbar width
  if (isContainerSmallerThanTabletPortrait.value) {
    const gap = 8
    const padding = 8
    const numberOfColumns = 2
    return (availableWidth - 2 * padding) / numberOfColumns - (numberOfColumns - 1) * gap
  }

  if (isContainerSmallerThanDesktop.value) {
    const numberOfColumns = isPostSizeStandard.value ? 4 : 3
    const maxWidth = isPostSizeStandard.value ? 256 : 380
    const gap = 16
    const padding = 24
    return Math.min((availableWidth - 2 * padding) / numberOfColumns - (numberOfColumns - 1) * gap, maxWidth)
  }

  if (isPostSizeStandard.value) {
    return NON_STREAM_STANDARD_MAX_POST_SIZE
  }

  const numberOfColumns = 4
  const gap = 16 // 48px padding around and 16px column gap
  const padding = 48
  return Math.min(
    (availableWidth - 2 * padding) / numberOfColumns - (numberOfColumns - 1) * gap,
    NON_STREAM_WIDE_MAX_POST_SIZE,
  )
})

const renderImmediately = (postIndex: number) => {
  if (!isAppUsing('lazyRenderPosts')) return true
  // For mobile, we want the first two posts to render ASAP.
  if (isContainerSmallerThanTabletPortrait.value && postIndex < 2) return true
  // For Tablet portrait and bigger, we want the first four posts to render ASAP.
  if (postIndex < 4) return true
  return false
}
</script>

<template>
  <section
    :id="'section-' + group.id"
    :key="group.id"
    :data-id="group.id"
    :data-rank="group.sort_index"
    :class="[
      'relative',
      'flex',
      'flex-col',
      'w-full',
      {
        'surface-section': wishGroupBy === WallWishGroupByTypes.Section,
      },
    ]"
  >
    <!-- DRAG AND DROP OVERLAY -->
    <div
      v-if="canShowDraggingOverlayOnGroup(group.id)"
      :class="[
        'absolute -mt-4 z-underneath',
        'border-solid border-2 rounded-xl shadow-elevation-2',
        'pointer-events-none',
        {
          'border-grape-500 bg-modal-overlay-grape-alpha-20': backgroundLuminance === 'light',
          'border-canary-500 bg-modal-overlay-canary-alpha-20': backgroundLuminance === 'dark',
          '-ms-4 ': !isContainerSmallerThanTabletPortrait,
        },
      ]"
      :style="{
        height: `${overlayHeight}px`,
        width: isContainerSmallerThanTabletPortrait ? 'calc(100% - 4px)' : `${overlayWidth}px`,
      }"
    />
    <!-- GROUP HEADER -->
    <SurfacePostGroupHeaderSection
      v-if="isAppUsing('groupPostsV2') && !isGroupByNone && xSectionHeader"
      :section="group"
      :class="[
        // Mobile: max width goes to last post
        // minus half the radius of the post.
        // There's an extra 8px left padding
        // that we need to account for.
        'max-w-[calc(100%-.5rem-.375rem)]',
        // Tablet portrait & bigger: max width
        // is 2.5 posts wide. 2.5/4 = 62.5%
        '@tablet-portrait/surface-container:max-w-[62.5%]',
      ]"
    />
    <SurfacePostGroupHeaderSectionOld
      v-if="!isAppUsing('groupPostsV2') && !isGroupByNone && xSectionHeader"
      :section="group"
      :class="[
        // Mobile: max width goes to last post
        // minus half the radius of the post.
        // There's an extra 8px left padding
        // that we need to account for.
        'max-w-[calc(100%-.5rem-.375rem)]',
        // Tablet portrait & bigger: max width
        // is 2.5 posts wide. 2.5/4 = 62.5%
        '@tablet-portrait/surface-container:max-w-[62.5%]',
      ]"
    />

    <!-- POSTS IN GROUP -->
    <div
      :id="`group-posts-${group.id}`"
      :data-id="group.id"
      :class="[
        'flex',
        'flex-wrap',
        isPostSizeStandard && 'gap-2 @tablet-portrait/surface-container:gap-4 mx-2',
        isPostSizeWide &&
          'gap-2 @tablet-portrait/surface-container:gap-4 @desktop/surface-container:gap-x-4 @desktop/surface-container:gap-y-6',
        isSectioned && (allSortedPostsByGroup[group.id] || []).length && 'mt-4',
        'mx-2 @tablet-portrait/surface-container:mx-0',
        'group-posts',
      ]"
    >
      <template v-for="(post, index) in allSortedPostsByGroup[group.id]">
        <SurfaceLazy
          v-show="shouldDisplayPost(post.cid)"
          :key="post.cid"
          :data-cid="post.cid"
          :data-id="post.id"
          :class="[
            'wish-wrapper',
            'h-[min-content]',
            isPostSizeStandard &&
              'w-[calc(50%-4px)] @tablet-portrait/surface-container:w-[calc(25%-14px)] @tablet-portrait/surface-container:max-w-[256px]',
            isPostSizeWide &&
              'w-[calc(50%-4px)] @tablet-portrait/surface-container:w-[calc(33.3%-14px)] @desktop/surface-container:w-[calc(25%-14px)] @tablet-portrait/surface-container:max-w-[380px]',
            // need to account for gap between posts so right amount of posts fit in each row
          ]"
          :min-height="minHeight"
          :min-width="minWidth"
          :render-immediately="renderImmediately(index)"
        >
          <SurfacePost
            :class="[
              // Exclude this component from using styles in app/assets/stylesheets/3/surface/show/layouts/matrix.scss
              'wish-v2',
            ]"
            :index="index"
            :post="post"
            :post-reaction-ids="post.id ? reactionIdsByPostId[post.id] : []"
            :post-comment-ids="post.id ? commentIdsByPostId[post.id] : []"
          />
        </SurfaceLazy>
      </template>
    </div>
  </section>
</template>
